import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormFields } from "@/components/public/FormFields";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsultationForm() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/consultation/${token}`).then(({ data }) => {
      setForm(data);
      if (data.status === "completed") setDone(true);
      const pre = {};
      (data.elements || []).forEach((el) => { if (el.type === "client_details") { pre[`${el.id}_name`] = data.prefill?.name || ""; pre[`${el.id}_email`] = data.prefill?.email || ""; pre[`${el.id}_phone`] = data.prefill?.phone || ""; } });
      setAnswers((a) => ({ ...pre, ...a }));
    }).catch(() => setNotFound(true));
  }, [token]);

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const submit = async () => {
    for (const el of form.elements) {
      if (!el.required) continue;
      const v = answers[el.id];
      if (el.type === "checkbox" && !v) return toast.error(`Please tick: ${el.label}`);
      if (["short", "long", "date", "signature", "choose"].includes(el.type) && !String(v || "").trim()) return toast.error(`Please complete: ${el.label}`);
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/consultation/${token}/submit`, { answers });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit"); }
    finally { setSubmitting(false); }
  };

  if (notFound) return <Centered><p className="text-muted-foreground">This form link is invalid or has expired.</p></Centered>;
  if (!form) return <Centered><p className="text-muted-foreground">Loading…</p></Centered>;
  if (done) return <Centered><div className="text-center" data-testid="consult-done"><CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-3" /><h2 className="font-heading font-bold text-xl">Thank you!</h2><p className="text-muted-foreground mt-1">Your {form.name} has been submitted.</p></div></Centered>;

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4" data-testid="consult-form">
      <div className="max-w-xl mx-auto">
        {form.business?.logo_url && <img src={form.business.logo_url.startsWith("/") ? process.env.REACT_APP_BACKEND_URL + form.business.logo_url : form.business.logo_url} alt="" className="h-10 mx-auto mb-4 object-contain" />}
        <Card className="rounded-2xl border p-6 shadow-sm space-y-5">
          <h1 className="font-heading font-extrabold text-2xl">{form.name}</h1>
          <FormFields elements={form.elements} answers={answers} set={set} />
          <Button className="rounded-lg w-full" onClick={submit} disabled={submitting} data-testid="consult-submit">{submitting ? "Submitting…" : "Submit form"}</Button>
        </Card>
      </div>
    </div>
  );
}

const Centered = ({ children }) => <div className="min-h-screen flex items-center justify-center p-6">{children}</div>;
